import {
    useCallback,
    useEffect,
    useState,
    Fragment,
    useMemo,
    useRef
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    FlatList,
    Image,
    View
} from "react-native";
import type {
    MemberCreateModalProps,
    CompanySelectionType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    DateTimePicker,
    BottomSheet,
    SelectBox,
    TextInput,
    Seperator,
    Button,
    Switch,
    Dialog,
    Modal,
    Text
} from "ncore-ui-kit";
import type {
    GetAllCompaniesForAdminObjectType
} from "../../../../services/restAPI/actions/getAllCompaniesForAdmin/type";
import type {
    GetCitiesResponseType
} from "../../../../services/restAPI/actions/getCities/type";
import type {
    GetDepartmentsResponseType
} from "../../../../services/restAPI/actions/getDepartments/type";
import type {
    GetDistrictsResponseType
} from "../../../../services/restAPI/actions/getDistricts/type";
import type {
    GetFacultiesResponseType
} from "../../../../services/restAPI/actions/getFaculties/type";
import type {
    GetPositionsResponseType
} from "../../../../services/restAPI/actions/getPositions/type";
import type {
    GetSectorsResponseType
} from "../../../../services/restAPI/actions/getSectors/type";
import type {
    GetUniversitiesResponseType
} from "../../../../services/restAPI/actions/getUniversities/type";
import type {
    RegisterUserRequestType,
    UserHomeAddressRequest,
    UserEducationRequest
} from "../../../../services/restAPI/actions/registerUser/type";
import {
    Trash2Icon,
    PencilIcon,
    PlusIcon,
    SaveIcon,
    XIcon
} from "lucide-react-native";
import md5 from "md5";
import {
    passwordValidation,
    emailValidation,
    phoneValidation,
    tckValidation
} from "../../../../utils/validations";
import RESTServiceContext from "../../../../services/restAPI/context";

const MemberCreateModal = ({
    isVisible,
    onSuccess,
    onClose
}: MemberCreateModalProps) => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const [
        formData,
        setFormData
    ] = useState<Partial<RegisterUserRequestType> & { positionID?: string }>({
        existingCompanies: [],
        isStudent: false,
        newCompanies: []
    });

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isDialogVisible,
        setIsDialogVisible
    ] = useState<boolean>(false);

    const [
        positions,
        setPositions
    ] = useState<GetPositionsResponseType["payload"]>([]);

    const [
        sectors,
        setSectors
    ] = useState<GetSectorsResponseType["payload"]>([]);

    const [
        logoPreview,
        setLogoPreview
    ] = useState<string>("");

    const [
        logoFile,
        setLogoFile
    ] = useState<File | null>(null);

    const logoInputRef = useRef<HTMLInputElement | null>(null);
    const companyLogoInputRef = useRef<HTMLInputElement | null>(null);

    // Company selection states
    const [
        isCompanySheetVisible,
        setIsCompanySheetVisible
    ] = useState<boolean>(false);

    const [
        companies,
        setCompanies
    ] = useState<GetAllCompaniesForAdminObjectType[]>([]);

    const [
        companySearchText,
        setCompanySearchText
    ] = useState<string>("");

    const [
        companyPage,
        setCompanyPage
    ] = useState<number>(1);

    const [
        hasMoreCompanies,
        setHasMoreCompanies
    ] = useState<boolean>(true);

    const [
        loadingCompanies,
        setLoadingCompanies
    ] = useState<boolean>(false);

    const [
        selectedCompanies,
        setSelectedCompanies
    ] = useState<CompanySelectionType[]>([]);

    const [
        isNewCompanyModalVisible,
        setIsNewCompanyModalVisible
    ] = useState<boolean>(false);

    const [
        editCompanyIndex,
        setEditCompanyIndex
    ] = useState<number | null>(null);

    const [
        newCompanyDraft,
        setNewCompanyDraft
    ] = useState<CompanySelectionType | null>(null);

    const [
        isEducationSheetVisible,
        setIsEducationSheetVisible
    ] = useState<boolean>(false);

    const [
        editEducationIndex,
        setEditEducationIndex
    ] = useState<number | null>(null);

    const [
        newEducationDraft,
        setNewEducationDraft
    ] = useState<UserEducationRequest | null>(null);

    const [
        cities,
        setCities
    ] = useState<GetCitiesResponseType["payload"]>([]);

    const [
        districts,
        setDistricts
    ] = useState<GetDistrictsResponseType["payload"]>([]);

    const [
        companyDistricts,
        setCompanyDistricts
    ] = useState<GetDistrictsResponseType["payload"]>([]);

    const [
        universities,
        setUniversities
    ] = useState<GetUniversitiesResponseType["payload"]>([]);

    const [
        faculties,
        setFaculties
    ] = useState<GetFacultiesResponseType["payload"]>([]);

    const [
        departments,
        setDepartments
    ] = useState<GetDepartmentsResponseType["payload"]>([]);

    const updateHomeAddress = (field: keyof UserHomeAddressRequest, value: unknown) => {
        setFormData(prev => ({
            ...prev,
            homeAddress: {
                ...(prev.homeAddress || {
                    addressDetail: "",
                    neighborhood: "",
                    districtID: "",
                    cityID: ""
                }),
                [field]: value
            } as never
        }));
    };

    const fetchCities = async () => {
        try {
            const result = await RESTServiceContext.action("getCities", null);
            if (result.payload) setCities(result.payload);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchUniversities = async () => {
        try {
            const result = await RESTServiceContext.action("getUniversities", null);
            if (result.payload) setUniversities(result.payload);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (formData.homeAddress?.cityID) {
            updateHomeAddress("districtID", "");
            setDistricts([]);
            RESTServiceContext.action("getDistricts", {
                cityID: formData.homeAddress.cityID
            }).then(result => {
                if (result.payload) setDistricts(result.payload);
            });
        }
    }, [formData.homeAddress?.cityID]);

    useEffect(() => {
        if (newEducationDraft?.universityID) {
            setNewEducationDraft(prev => prev ? {
                ...prev,
                departmentID: "",
                facultyID: ""
            } : null);
            setFaculties([]);
            setDepartments([]);
            RESTServiceContext.action("getFaculties", {
                universityID: newEducationDraft.universityID
            }).then(result => {
                if (result.payload) setFaculties(result.payload);
            });
        }
    }, [newEducationDraft?.universityID]);

    useEffect(() => {
        if (newEducationDraft?.facultyID) {
            setNewEducationDraft(prev => prev ? {
                ...prev,
                departmentID: ""
            } : null);
            setDepartments([]);
            RESTServiceContext.action("getDepartments", {
                facultyID: newEducationDraft.facultyID
            }).then(result => {
                if (result.payload) setDepartments(result.payload);
            });
        }
    }, [newEducationDraft?.facultyID]);

    useEffect(() => {
        if (newCompanyDraft?.cityID) {
            setNewCompanyDraft(prev => prev ? {
                ...prev,
                districtID: ""
            } : null);
            setCompanyDistricts([]);
            RESTServiceContext.action("getDistricts", {
                cityID: newCompanyDraft.cityID
            }).then(result => {
                if (result.payload) setCompanyDistricts(result.payload);
            });
        }
    }, [newCompanyDraft?.cityID]);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    useEffect(() => {
        if (isVisible) {
            setFormData({
                existingCompanies: [],
                isStudent: false,
                newCompanies: [],
                education: []
            });
            setSelectedCompanies([]);
            setLogoFile(null);
            setLogoPreview("");
            fetchPositions();
            fetchSectors();
            fetchCities();
            fetchUniversities();
        }
    }, [isVisible]);

    const fetchPositions = async () => {
        try {
            const result = await RESTServiceContext.action("getPositions", null);
            if (result.payload) {
                setPositions(result.payload);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchSectors = async () => {
        try {
            const result = await RESTServiceContext.action("getSectors", null);
            if (result.payload) {
                setSectors(result.payload);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchCompanies = useCallback(async (page: number, isReset: boolean = false) => {
        try {
            setLoadingCompanies(true);
            const requestParams: Record<string, unknown> = {
                page
            };
            if (companySearchText) {
                requestParams.searchText = companySearchText;
            }

            const result = await RESTServiceContext.action("getAllCompaniesForAdmin", requestParams);
            if (result.payload) {
                if (result.payload.length < 20) {
                    setHasMoreCompanies(false);
                } else {
                    setHasMoreCompanies(true);
                }

                if (isReset) {
                    setCompanies(result.payload);
                } else {
                    setCompanies(prev => [
                        ...prev,
                        ...result.payload!
                    ]);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingCompanies(false);
        }
    }, [companySearchText]);

    useEffect(() => {
        if (isCompanySheetVisible) {
            fetchCompanies(companyPage, companyPage === 1);
        }
    }, [
        fetchCompanies,
        companyPage,
        isCompanySheetVisible
    ]);

    const handleSearchCompany = (text: string) => {
        setCompanySearchText(text);
        setCompanyPage(1);
        setHasMoreCompanies(true);
    };

    const updateField = (field: keyof RegisterUserRequestType | "positionID", value: unknown) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const maxFileSize = 5 * 1024 * 1024;
            if (file.size > maxFileSize) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-invalid-file-size") || "Dosya boyutu 5 MB'dan küçük olmalıdır."
                });
                return;
            }
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleCompanyLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const maxFileSize = 5 * 1024 * 1024;
            if (file.size > maxFileSize) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-invalid-file-size") || "Dosya boyutu 5 MB'dan küçük olmalıdır."
                });
                return;
            }
            setNewCompanyDraft(prev => prev ? {
                ...prev,
                logoPreview: URL.createObjectURL(file),
                logoFile: file
            } : null);
        }
    };

    const uploadPhoto = async (
        targetUserID: string,
        file: File
    ) => {
        try {
            const result = await RESTServiceContext.action("getUploadTokenForUserProfilePhoto", {
                userID: targetUserID
            });
            if (result.payload?.token) {
                const formDataToUpload = new FormData();
                formDataToUpload.append("file", file);
                await fetch(`${process.env.STORAGE_URL}/upload`, {
                    headers: {
                        Authorization: result.payload.token
                    },
                    body: formDataToUpload,
                    method: "POST"
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const uploadCompanyPhoto = async (
        targetCompanyID: string,
        file: File
    ) => {
        try {
            const result = await RESTServiceContext.action("getUploadTokenForCompanyPhoto", {
                companyID: targetCompanyID
            });
            if (result.payload?.token) {
                const formDataToUpload = new FormData();
                formDataToUpload.append("file", file);
                await fetch(`${process.env.STORAGE_URL}/upload`, {
                    headers: {
                        Authorization: result.payload.token
                    },
                    body: formDataToUpload,
                    method: "POST"
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleConfirmSave = async () => {
        setIsDialogVisible(false);
        try {
            setIsSubmitting(true);
            const existingCompanies = selectedCompanies.filter(c => !c.isNew).map(c => ({
                positionID: c.positionID || "",
                companyID: c._id
            }));
            const newCompanies = selectedCompanies.filter(c => c.isNew).map(c => ({
                foundationYear: c.foundationYear || new Date().getFullYear(),
                phoneNumber: c.phoneNumber || "1111111111",
                mail: c.mail || formData.mail || "",
                neighborhood: c.neighborhood || "",
                isExports: c.isExports || false,
                districtID: c.districtID || "",
                positionID: c.positionID || "",
                sectorIDs: c.sectorIDs || [],
                taxNumber: c.taxNumber || "",
                taxOffice: c.taxOffice || "",
                aboutUs: c.aboutUs || "",
                address: c.address || "",
                website: c.website || "",
                cityID: c.cityID || "",
                title: c.title
            }));

            const payload: RegisterUserRequestType = {
                ...formData as RegisterUserRequestType,
                existingCompanies: existingCompanies.filter(Boolean),
                education: formData.education?.filter(Boolean),
                newCompanies: newCompanies.filter(Boolean),
                password: md5(formData.password as string)
            };

            const result = await RESTServiceContext.action("registerUser", payload);
            if (result.code === 200 || result.payload) {
                const newUserID = result.payload?.userID;
                const createdCompanyIDs = result.payload?.createdCompanyIDs || [];

                if (newUserID && logoFile) {
                    await uploadPhoto(newUserID, logoFile);
                }

                const newCompsList = selectedCompanies.filter(c => c.isNew);
                for (let i = 0; i < newCompsList.length; i++) {
                    const comp = newCompsList[i];
                    const companyID = createdCompanyIDs[i];
                    if (comp && comp.logoFile && companyID) {
                        await uploadCompanyPhoto(companyID, comp.logoFile);
                    }
                }

                NCoreUIKitSnackBar.open({
                    title: localize("members-success-create") || "Üye başarıyla eklendi."
                });
                onSuccess();
                onClose();
            } else {
                NCoreUIKitSnackBar.open({
                    title: localize("members-error-save") || "Üye eklenirken hata oluştu."
                });
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("members-error-save") || "Üye eklenirken hata oluştu."
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFormValid = useMemo(() => {
        if (!formData.firstName || formData.firstName.trim().length < 2) return false;
        if (!formData.lastName || formData.lastName.trim().length < 2) return false;
        if (!formData.tcNo || !tckValidation(formData.tcNo)) return false;
        if (!formData.memberCode) return false;
        if (!formData.mail || !emailValidation(formData.mail)) return false;
        if (!formData.phoneNumber || !phoneValidation(formData.phoneNumber)) return false;
        if (!formData.password || !passwordValidation(formData.password)) return false;
        if (!formData.registrationDate) return false;

        const newComps = selectedCompanies.filter(c => c.isNew);
        for (const comp of newComps) {
            if (!comp.taxNumber || !comp.taxOffice || !comp.address || !comp.phoneNumber || !comp.mail || !comp.sectorIDs || comp.sectorIDs.length === 0 || !comp.title || !comp.foundationYear || !comp.positionID || comp.isExports === undefined || !comp.aboutUs) {
                return false;
            }
        }

        if (formData.homeAddress?.cityID || formData.homeAddress?.districtID || formData.homeAddress?.neighborhood) {
            if (!formData.homeAddress.cityID || !formData.homeAddress.districtID || !formData.homeAddress.neighborhood) return false;
        }

        if (formData.education && formData.education.length > 0) {
            for (const edu of formData.education) {
                if (!edu.universityID || !edu.facultyID || !edu.departmentID) return false;
            }
        }

        return true;
    }, [
        formData,
        selectedCompanies
    ]);

    const handleSubmit = () => {
        if (!formData.firstName || !formData.lastName || !formData.mail || !formData.tcNo || !formData.memberCode || !formData.phoneNumber || !formData.password || !formData.registrationDate) {
            NCoreUIKitSnackBar.open({
                title: localize("events-alert-fields-required")
            });
            return;
        }

        if (formData.password && !passwordValidation(formData.password)) {
            NCoreUIKitSnackBar.open({
                title: localize("members-invalid-password") || "Şifreniz en az 8 karakter uzunluğunda olmalı ve en az bir büyük harf, bir küçük harf, bir rakam ve bir özel karakter içermelidir."
            });
            return;
        }

        if (!tckValidation(formData.tcNo)) {
            NCoreUIKitSnackBar.open({
                title: localize("members-invalid-tckn") || "Geçersiz TCKN formatı."
            });
            return;
        }

        if (!emailValidation(formData.mail)) {
            NCoreUIKitSnackBar.open({
                title: localize("members-invalid-email") || "Geçersiz e-posta formatı."
            });
            return;
        }

        if (!phoneValidation(formData.phoneNumber)) {
            NCoreUIKitSnackBar.open({
                title: localize("members-invalid-phone") || "Geçersiz telefon formatı."
            });
            return;
        }

        const newComps = selectedCompanies.filter(c => c.isNew);
        for (const comp of newComps) {
            if (!comp.taxNumber || !comp.taxOffice || !comp.address || !comp.phoneNumber || !comp.mail || !comp.sectorIDs || comp.sectorIDs.length === 0) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-new-company-fields-required") || "Lütfen yeni eklediğiniz şirketin zorunlu alanlarını doldurun."
                });
                return;
            }
        }

        setIsDialogVisible(true);
    };

    return <Fragment>
        <Modal
            portalName="modal-system"
            id="member-create-modal"
            isActive={isVisible}
            onClosed={onClose}
        >
            <ScrollView
                style={stylesheet.container}
            >
                <View
                    style={{
                        padding: isMobile ? spaces.spacingMd : spaces.spacingXl,
                        gap: isMobile ? spaces.spacingMd : spaces.spacingXl,
                        backgroundColor: colors.project.brandWhite,
                        borderRadius: radiuses.lg
                    }}
                >
                    <View
                        style={{
                            justifyContent: "space-between",
                            flexDirection: "row",
                            alignItems: "center"
                        }}
                    >
                        <Text
                            variant="titleLargeSize"
                        >
                            {localize("members-create-new")}
                        </Text>
                        <TouchableOpacity
                            onPress={onClose}
                        >
                            <XIcon
                                color={colors.content.icon.mid}
                                size={24}
                            />
                        </TouchableOpacity>
                    </View>
                    {/* IMAGES */}
                    <View
                        style={{
                            alignItems: "center",
                            gap: spaces.spacingMd
                        }}
                    >
                        <TouchableOpacity
                            onPress={() => logoInputRef.current?.click()}
                        >
                            {
                                logoPreview ?
                                    <Image
                                        style={[
                                            stylesheet.logoPreview,
                                            {
                                                borderRadius: radiuses.full
                                            }
                                        ]}
                                        source={{
                                            uri: logoPreview
                                        }}
                                    />
                                :
                                    <View
                                        style={[
                                            stylesheet.logoPlaceholder,
                                            {
                                                backgroundColor: colors.project.brandBorder,
                                                borderRadius: radiuses.full
                                            }
                                        ]}
                                    >
                                        <PlusIcon
                                            color={colors.content.icon.mid}
                                            size={24}
                                        />
                                    </View>
                            }
                        </TouchableOpacity>
                        <Text
                            customColor="content.text.mid"
                            variant="bodySmallSize"
                        >
                            {localize("members-form-profile-photo") || "Profil Fotoğrafı"}
                        </Text>
                        <input
                            onChange={handleLogoSelect}
                            style={{
                                display: "none"
                            }}
                            ref={logoInputRef}
                            accept="image/*"
                            type="file"
                        />
                    </View>
                    <Seperator
                        color="subtle"
                    />
                    {/* BASIC INFO */}
                    <View
                        style={[
                            stylesheet.formContainer,
                            {
                                gap: spaces.spacingLg
                            }
                        ]}
                    >
                        <Text
                            variant="titleMediumSize"
                        >
                            {localize("members-form-basic-info")}
                        </Text>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <TextInput
                                type={(formData.firstName || "").length > 0 && (formData.firstName || "").trim().length < 2 ? "danger" : "default"}
                                onChangeText={(text) => updateField("firstName", text)}
                                title={localize("members-form-firstname")}
                                value={formData.firstName || ""}
                                spreadBehaviour="stretch"
                            />
                            <TextInput
                                type={(formData.lastName || "").length > 0 && (formData.lastName || "").trim().length < 2 ? "danger" : "default"}
                                onChangeText={(text) => updateField("lastName", text)}
                                title={localize("members-form-lastname")}
                                value={formData.lastName || ""}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <TextInput
                                type={(formData.tcNo || "").length > 0 && !tckValidation(formData.tcNo || "") ? "danger" : "default"}
                                onChangeText={(text) => updateField("tcNo", text)}
                                title={localize("members-form-tcno")}
                                value={formData.tcNo || ""}
                                spreadBehaviour="stretch"
                            />
                            <TextInput
                                type={(formData.memberCode || "").length > 0 && (formData.memberCode || "").trim().length < 2 ? "danger" : "default"}
                                onChangeText={(text) => updateField("memberCode", text)}
                                title={localize("members-form-membercode")}
                                value={formData.memberCode || ""}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <TextInput
                                type={(formData.mail || "").length > 0 && !emailValidation(formData.mail || "") ? "danger" : "default"}
                                onChangeText={(text) => updateField("mail", text)}
                                title={localize("members-form-mail")}
                                value={formData.mail || ""}
                                spreadBehaviour="stretch"
                            />
                            <TextInput
                                type={(formData.phoneNumber || "").length > 0 && !phoneValidation(formData.phoneNumber || "") ? "danger" : "default"}
                                onChangeText={(text) => updateField("phoneNumber", text)}
                                title={localize("members-form-phone")}
                                value={formData.phoneNumber || ""}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <DateTimePicker
                                onChange={(val) => {
                                    if (val.date) {
                                        updateField("birthDate", typeof val.date === "string" ? val.date : val.date.toISOString());
                                    } else {
                                        updateField("birthDate", "");
                                    }
                                }}
                                initialDate={formData.birthDate ? new Date(formData.birthDate) : undefined}
                                title={localize("members-form-birthdate")}
                                dateTimeSheetProps={{
                                    portalName: "modal-system"
                                }}
                                isWorkWithRealtime={false}
                                spreadBehaviour="stretch"
                                titleFormat="DD.MM.YYYY"
                                placeholder="gg.aa.yyyy"
                                pickerType="date"
                            />
                            <DateTimePicker
                                onChange={(val) => {
                                    if (val.date) {
                                        updateField("registrationDate", typeof val.date === "string" ? val.date : val.date.toISOString());
                                    } else {
                                        updateField("registrationDate", "");
                                    }
                                }}
                                initialDate={formData.registrationDate ? new Date(formData.registrationDate) : undefined}
                                title={localize("members-form-registrationdate") || "Kayıt Tarihi"}
                                dateTimeSheetProps={{
                                    portalName: "modal-system"
                                }}
                                isWorkWithRealtime={false}
                                spreadBehaviour="stretch"
                                titleFormat="DD.MM.YYYY"
                                placeholder="gg.aa.yyyy"
                                pickerType="date"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <TextInput
                                type={(formData.password || "").length > 0 && !passwordValidation(formData.password || "") ? "danger" : "default"}
                                onChangeText={(text) => updateField("password", text)}
                                title={localize("members-form-password") || "Şifre"}
                                value={formData.password || ""}
                                spreadBehaviour="stretch"
                                secureTextEntry={true}
                            />
                            <TextInput
                                title={localize("members-form-birthplace") || "Doğum Yeri"}
                                onChangeText={(text) => updateField("birthPlace", text)}
                                value={formData.birthPlace || ""}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <TextInput
                                title={localize("members-form-fathername") || "Baba Adı"}
                                onChangeText={(text) => updateField("fatherName", text)}
                                value={formData.fatherName || ""}
                                spreadBehaviour="stretch"
                            />
                            <TextInput
                                title={localize("members-form-mothername") || "Anne Adı"}
                                onChangeText={(text) => updateField("motherName", text)}
                                value={formData.motherName || ""}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <TextInput
                                title={localize("members-form-maritalstatus") || "Medeni Durum"}
                                onChangeText={(text) => updateField("maritalStatus", text)}
                                value={formData.maritalStatus || ""}
                                spreadBehaviour="stretch"
                            />
                            <TextInput
                                title={localize("members-form-gender") || "Cinsiyet"}
                                onChangeText={(text) => updateField("gender", text)}
                                value={formData.gender || ""}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <TextInput
                                title={localize("members-form-bloodtype") || "Kan Grubu"}
                                onChangeText={(text) => updateField("bloodType", text)}
                                value={formData.bloodType || ""}
                                spreadBehaviour="stretch"
                            />
                            <TextInput
                                title={localize("members-form-aboutmytrade") || "Ticaretim Hakkında"}
                                onChangeText={(text) => updateField("aboutMyTrade", text)}
                                value={formData.aboutMyTrade || ""}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <SelectBox
                                initialSelectedItems={formData.homeAddress?.cityID ? [
                                    {
                                        __title: cities?.find(c => c._id === formData.homeAddress?.cityID)?.value || "",
                                        __key: formData.homeAddress.cityID
                                    }
                                ] : []}
                                onChange={selectedItems => {
                                    const item = selectedItems[0];
                                    if (item) {
                                        updateHomeAddress("cityID", item.__key);
                                    } else {
                                        updateHomeAddress("cityID", "");
                                    }
                                    updateHomeAddress("districtID", "");
                                }}
                                title={localize("members-form-city") || "İl"}
                                key={`city-select-${cities?.length || 0}`}
                                titleExtractor={item => item.title}
                                keyExtractor={item => item.value}
                                data={(cities || []).map(c => ({
                                    title: c.value,
                                    value: c._id
                                }))}
                                selectSheetProps={{
                                    portalName: "modal-system"
                                }}
                                spreadBehaviour="stretch"
                            />
                            <SelectBox
                                initialSelectedItems={formData.homeAddress?.districtID ? [
                                    {
                                        __title: districts?.find(d => d._id === formData.homeAddress?.districtID)?.value || "",
                                        __key: formData.homeAddress.districtID
                                    }
                                ] : []}
                                onChange={selectedItems => {
                                    const item = selectedItems[0];
                                    if (item) {
                                        updateHomeAddress("districtID", item.__key);
                                    } else {
                                        updateHomeAddress("districtID", "");
                                    }
                                }}
                                title={localize("members-form-district") || "İlçe"}
                                key={`district-select-${districts?.length || 0}`}
                                isDisabled={!formData.homeAddress?.cityID}
                                titleExtractor={item => item.title}
                                data={(districts || []).map(d => ({
                                    title: d.value,
                                    value: d._id
                                }))}
                                keyExtractor={item => item.value}
                                selectSheetProps={{
                                    portalName: "modal-system"
                                }}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <TextInput
                                onChangeText={(text) => updateHomeAddress("neighborhood", text)}
                                title={localize("members-form-neighborhood") || "Mahalle"}
                                value={formData.homeAddress?.neighborhood || ""}
                                spreadBehaviour="stretch"
                            />
                            <TextInput
                                onChangeText={(text) => updateHomeAddress("addressDetail", text)}
                                title={localize("members-form-addressdetail") || "Adres Detayı"}
                                value={formData.homeAddress?.addressDetail || ""}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <Seperator
                            color="subtle"
                        />
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    justifyContent: "space-between",
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <Text
                                variant="titleMediumSize"
                            >
                                {localize("members-form-education") || "Eğitim Durumu"}
                            </Text>
                            <Button
                                title={localize("members-form-education") || "Eğitim Ekle"}
                                onPress={() => setIsEducationSheetVisible(true)}
                                icon={({
                                    size
                                }) => <PlusIcon
                                    color={colors.project.brandWhite}
                                    size={size}
                                />}
                                customBorderColor="secondaryButton"
                                customColor="secondaryButton"
                                spreadBehaviour="free"
                                variant="filled"
                                size="small"
                            />
                        </View>
                        <View
                            style={{
                                gap: spaces.spacingMd
                            }}
                        >
                            {
                                (formData.education || []).map((edu, index) => {
                                    return <View
                                        style={[
                                            stylesheet.companyCard,
                                            {
                                                borderColor: colors.project.brandBorder,
                                                borderWidth: borders.seperator,
                                                padding: spaces.spacingMd,
                                                borderRadius: radiuses.md
                                            }
                                        ]}
                                        key={`edu-${index}`}
                                    >
                                        <View
                                            style={[
                                                stylesheet.row,
                                                {
                                                    gap: spaces.spacingMd
                                                }
                                            ]}
                                        >
                                            <Text
                                                variant="bodyMediumSize"
                                            >
                                                {universities?.find(u => u._id === edu.universityID)?.value || edu.universityID} {edu.graduationYear ? `- ${edu.graduationYear}` : ""}
                                            </Text>
                                        </View>
                                        <View
                                            style={[
                                                stylesheet.row,
                                                {
                                                    right: spaces.spacingMd,
                                                    top: spaces.spacingMd,
                                                    position: "absolute",
                                                    gap: spaces.spacingSm
                                                }
                                            ]}
                                        >
                                            <TouchableOpacity
                                                onPress={() => {
                                                    setEditEducationIndex(index);
                                                    setNewEducationDraft(edu);
                                                    setIsEducationSheetVisible(true);
                                                }}
                                            >
                                                <PencilIcon
                                                    color={colors.content.icon.mid}
                                                    size={20}
                                                />
                                            </TouchableOpacity>
                                            <TouchableOpacity
                                                onPress={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        education: prev.education?.filter((_, i) => i !== index)
                                                    }));
                                                }}
                                            >
                                                <Trash2Icon
                                                    color={colors.content.icon.mid}
                                                    size={20}
                                                />
                                            </TouchableOpacity>
                                        </View>
                                    </View>;
                                })
                            }
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.row,
                                    {
                                        justifyContent: "space-between",
                                        gap: spaces.spacingMd,
                                        flex: 1
                                    }
                                ]}
                            >
                                <Text
                                    variant="bodyMediumSize"
                                >
                                    {localize("members-form-isstudent")}
                                </Text>
                                <Switch
                                    onPress={() => updateField("isStudent", !formData.isStudent)}
                                    isActive={formData.isStudent || false}
                                    customTheme={{
                                        themeKey: "events_form" as never
                                    }}
                                    type="neutral"
                                />
                            </View>
                        </View>
                    </View>
                    <Seperator
                        color="subtle"
                    />
                    {/* COMPANY */}
                    <View
                        style={[
                            stylesheet.formContainer,
                            {
                                gap: spaces.spacingLg
                            }
                        ]}
                    >
                        <View
                            style={stylesheet.headerContainer}
                        >
                            <Text
                                variant="titleMediumSize"
                            >
                                {localize("members-form-companies") || "Şirketler"}
                            </Text>
                            <Button
                                title={localize("members-add-company") || "Şirket Ekle"}
                                onPress={() => setIsCompanySheetVisible(true)}
                                icon={({
                                    size
                                }) => <PlusIcon
                                    color={colors.project.brandWhite}
                                    size={size}
                                />}
                                customBorderColor="secondaryButton"
                                customColor="secondaryButton"
                                spreadBehaviour="free"
                                variant="filled"
                                size="small"
                            />
                        </View>
                        <View
                            style={{
                                gap: spaces.spacingMd
                            }}
                        >
                            {
                                selectedCompanies.map((comp, index) => {
                                    return <View
                                        style={[
                                            stylesheet.companyCard,
                                            {
                                                borderColor: colors.project.brandBorder,
                                                borderWidth: borders.seperator,
                                                padding: spaces.spacingMd,
                                                borderRadius: radiuses.md
                                            }
                                        ]}
                                        key={`comp-${index}`}
                                    >
                                        <View
                                            style={[
                                                stylesheet.row,
                                                {
                                                    gap: spaces.spacingMd
                                                }
                                            ]}
                                        >
                                            <Text
                                                variant="bodyMediumSize"
                                            >
                                                {comp.title}
                                            </Text>
                                            {
                                                comp.isNew ?
                                                    <View
                                                        style={[
                                                            stylesheet.newChip,
                                                            {
                                                                backgroundColor: colors.system.default.success,
                                                                paddingHorizontal: spaces.spacingSm,
                                                                paddingVertical: spaces.spacingXs,
                                                                borderRadius: radiuses.full
                                                            }
                                                        ]}
                                                    >
                                                        <Text
                                                            customColor="brandWhite"
                                                            variant="bodySmallSize"
                                                        >
                                                            {localize("new") || "Yeni"}
                                                        </Text>
                                                    </View>
                                                :
                                                    null
                                            }
                                        </View>
                                        <View
                                            style={[
                                                stylesheet.row,
                                                {
                                                    right: spaces.spacingMd,
                                                    top: spaces.spacingMd,
                                                    position: "absolute",
                                                    gap: spaces.spacingSm
                                                }
                                            ]}
                                        >
                                            {
                                                comp.isNew ?
                                                    <TouchableOpacity
                                                        onPress={() => {
                                                            setEditCompanyIndex(index);
                                                            setNewCompanyDraft(comp);
                                                            setIsNewCompanyModalVisible(true);
                                                        }}
                                                    >
                                                        <PencilIcon
                                                            color={colors.content.icon.mid}
                                                            size={20}
                                                        />
                                                    </TouchableOpacity>
                                                :
                                                    null
                                            }
                                            <TouchableOpacity
                                                onPress={() => {
                                                    setSelectedCompanies(prev => prev.filter((_, i) => i !== index));
                                                }}
                                            >
                                                <XIcon
                                                    color={colors.content.icon.danger}
                                                    size={20}
                                                />
                                            </TouchableOpacity>
                                        </View>
                                        <View
                                            style={{
                                                marginTop: spaces.spacingMd
                                            }}
                                        >
                                            <SelectBox
                                                placeholder={localize("members-form-position") || "Pozisyon"}
                                                title={localize("members-form-position") || "Pozisyon"}
                                                onChange={selectedItems => {
                                                    const item = selectedItems[0];
                                                    if (item) {
                                                        setSelectedCompanies(prev => {
                                                            const arr = [
                                                                ...prev
                                                            ];
                                                            const comp = arr[index];
                                                            if (comp) {
                                                                comp.positionID = item.__key;
                                                            }
                                                            return arr;
                                                        });
                                                    }
                                                }}
                                                initialSelectedItems={comp.positionID ? [
                                                    {
                                                        __key: comp.positionID,
                                                        __title: ""
                                                    }
                                                ] : []}
                                                titleExtractor={item => item.title}
                                                keyExtractor={item => item.value}
                                                selectSheetProps={{
                                                    portalName: "modal-system"
                                                }}
                                                data={positions.map(p => ({
                                                    title: p.value,
                                                    value: p._id
                                                }))}
                                                spreadBehaviour="stretch"
                                            />
                                        </View>
                                    </View>;
                                })
                            }
                        </View>
                    </View>
                    <View
                        style={[
                            stylesheet.footerContainer,
                            {
                                flexDirection: isMobile ? "column-reverse" : "row",
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Button
                            title={localize("members-form-cancel") || "İptal"}
                            spreadBehaviour={isMobile ? "stretch" : "free"}
                            customBorderColor="secondaryButton"
                            customColor="secondaryButton"
                            onPress={onClose}
                            variant="filled"
                        />
                        <Button
                            icon={({
                                color,
                                size
                            }) => <SaveIcon
                                color={colors.project.brandWhite || colors.content.icon[color]}
                                size={size}
                            />}
                            title={localize("members-form-save") || "Kaydet"}
                            spreadBehaviour={isMobile ? "stretch" : "free"}
                            customTextColor={colors.project.brandWhite}
                            isDisabled={!isFormValid || isSubmitting}
                            isLoading={isSubmitting}
                            customBorderColor="gold"
                            onPress={handleSubmit}
                            customColor="gold"
                            variant="filled"
                        />
                    </View>
                </View>
            </ScrollView>
        </Modal>
        <Dialog
            content={localize("members-form-dialog-content") || "Üyeyi sisteme kaydetmek istediğinize emin misiniz?"}
            primaryButtonProps={{
                title: localize("members-form-dialog-yes") || "Evet, Kaydet",
                onPress: handleConfirmSave
            }}
            secondaryButtonProps={{
                title: localize("members-form-dialog-no") || "Hayır",
                onPress: () => setIsDialogVisible(false)
            }}
            isVisible={isDialogVisible}
            variant="yes-no"
        />
        <BottomSheet
            onClose={() => setIsCompanySheetVisible(false)}
            isActive={isCompanySheetVisible}
            portalName="modal-system"
        >
            <View
                style={[
                    stylesheet.sheetContent,
                    {
                        backgroundColor: colors.project.brandWhite,
                        padding: spaces.spacingXl,
                        borderRadius: radiuses.lg,
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("members-select-company") || "Firma Seç veya Ekle"}
                </Text>
                <TextInput
                    placeholder={localize("search-company") || "Firma Ara veya Yeni Ekle"}
                    onChangeText={handleSearchCompany}
                    value={companySearchText}
                    spreadBehaviour="stretch"
                />
                <FlatList
                    renderItem={({
                        item
                    }) => <TouchableOpacity
                        onPress={() => {
                            if (!selectedCompanies.find(c => c._id === item._id)) {
                                setSelectedCompanies(prev => [
                                    ...prev,
                                    {
                                        ...item,
                                        isNew: false
                                    }
                                ]);
                            }
                            setIsCompanySheetVisible(false);
                        }}
                        style={{
                            borderBottomColor: colors.project.brandBorder,
                            borderBottomWidth: borders.seperator,
                            paddingVertical: spaces.spacingMd
                        }}
                    >
                        <Text
                            variant="bodyMediumSize"
                        >
                            {item.title}
                        </Text>
                    </TouchableOpacity>}
                    onEndReached={() => {
                        if (hasMoreCompanies && !loadingCompanies) {
                            setCompanyPage(prev => prev + 1);
                        }
                    }}
                    ListFooterComponent={
                        loadingCompanies ?
                            <ActivityIndicator
                                style={{
                                    margin: spaces.spacingMd
                                }}
                                color={colors.project.gold}
                            />
                        :
                            null
                    }
                    keyExtractor={item => item._id}
                    onEndReachedThreshold={0.5}
                    data={companies}
                />
                {
                    companySearchText.trim().length > 0 && !companies.find(c => c.title.toLowerCase() === companySearchText.toLowerCase()) ?
                        <Button
                            title={`"${companySearchText}" Olarak Ekle`}
                            customTextColor={colors.project.brandWhite}
                            onPress={() => {
                                setEditCompanyIndex(null);
                                setNewCompanyDraft({
                                    _id: `new-${Date.now()}`,
                                    title: companySearchText,
                                    isNew: true
                                });
                                setIsNewCompanyModalVisible(true);
                                setIsCompanySheetVisible(false);
                            }}
                            spreadBehaviour="stretch"
                            customBorderColor="gold"
                            customColor="gold"
                            variant="filled"
                        />
                    :
                        null
                }
            </View>
        </BottomSheet>
        <Modal
            onClosed={() => {
                setIsNewCompanyModalVisible(false);
                setEditCompanyIndex(null);
                setNewCompanyDraft(null);
            }}
            isActive={isNewCompanyModalVisible}
            portalName="modal-system"
            id="new-company-modal"
        >
            <ScrollView
                style={stylesheet.container}
            >
                <View
                    style={{
                        backgroundColor: colors.project.brandWhite,
                        padding: spaces.spacingXl,
                        borderRadius: radiuses.lg,
                        gap: spaces.spacingXl
                    }}
                >
                    <View
                        style={{
                            justifyContent: "space-between",
                            flexDirection: "row",
                            alignItems: "center"
                        }}
                    >
                        <Text
                            variant="titleLargeSize"
                        >
                            {localize("members-add-company") || "Şirket Ekle"}
                        </Text>
                        <TouchableOpacity
                            onPress={() => setIsNewCompanyModalVisible(false)}
                        >
                            <XIcon
                                color={colors.content.icon.mid}
                                size={24}
                            />
                        </TouchableOpacity>
                    </View>
                    <View
                        key={editCompanyIndex !== null ? editCompanyIndex : "new"}
                        style={{
                            gap: spaces.spacingMd
                        }}
                    >
                        <View
                            style={{
                                justifyContent: "center",
                                alignItems: "center",
                                gap: spaces.spacingMd
                            }}
                        >
                            <TouchableOpacity
                                onPress={() => companyLogoInputRef.current?.click()}
                            >
                                {
                                    newCompanyDraft?.logoPreview ?
                                        <Image
                                            source={{
                                                uri: newCompanyDraft.logoPreview
                                            }}
                                            style={[
                                                stylesheet.logoPreview,
                                                {
                                                    borderRadius: radiuses.full
                                                }
                                            ]}
                                        />
                                    :
                                        <View
                                            style={[
                                                stylesheet.logoPlaceholder,
                                                {
                                                    backgroundColor: colors.project.brandBorder,
                                                    borderRadius: radiuses.full
                                                }
                                            ]}
                                        >
                                            <PlusIcon
                                                color={colors.content.icon.mid}
                                                size={24}
                                            />
                                        </View>
                                }
                            </TouchableOpacity>
                            <Text
                                customColor="content.text.mid"
                                variant="bodySmallSize"
                            >
                                {"Şirket Logosu"}
                            </Text>
                            <input
                                onChange={handleCompanyLogoSelect}
                                ref={companyLogoInputRef}
                                style={{
                                    display: "none"
                                }}
                                accept="image/*"
                                type="file"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <TextInput
                                type={(newCompanyDraft?.taxNumber || "").length > 0 && (newCompanyDraft?.taxNumber || "").trim().length < 2 ? "danger" : "default"}
                                title={localize("members-form-taxnumber") || "Vergi Numarası"}
                                onChangeText={(text) => setNewCompanyDraft(prev => prev ? {
                                    ...prev,
                                    taxNumber: text
                                } : null)}
                                value={newCompanyDraft?.taxNumber || ""}
                                spreadBehaviour="stretch"
                            />
                            <TextInput
                                type={(newCompanyDraft?.taxOffice || "").length > 0 && (newCompanyDraft?.taxOffice || "").trim().length < 2 ? "danger" : "default"}
                                title={localize("members-form-taxoffice") || "Vergi Dairesi"}
                                onChangeText={(text) => setNewCompanyDraft(prev => prev ? {
                                    ...prev,
                                    taxOffice: text
                                } : null)}
                                value={newCompanyDraft?.taxOffice || ""}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <TextInput
                                type={(newCompanyDraft?.phoneNumber || "").length > 0 && !phoneValidation(newCompanyDraft?.phoneNumber || "") ? "danger" : "default"}
                                onChangeText={(text) => setNewCompanyDraft(prev => prev ? {
                                    ...prev,
                                    phoneNumber: text
                                } : null)}
                                title={localize("members-form-phone") || "Telefon"}
                                value={newCompanyDraft?.phoneNumber || ""}
                                spreadBehaviour="stretch"
                            />
                            <TextInput
                                type={(newCompanyDraft?.mail || "").length > 0 && !emailValidation(newCompanyDraft?.mail || "") ? "danger" : "default"}
                                onChangeText={(text) => setNewCompanyDraft(prev => prev ? {
                                    ...prev,
                                    mail: text
                                } : null)}
                                title={localize("members-form-mail") || "E-Posta"}
                                value={newCompanyDraft?.mail || ""}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <SelectBox
                                initialSelectedItems={newCompanyDraft?.cityID ? [
                                    {
                                        __title: cities?.find(c => c._id === newCompanyDraft?.cityID)?.value || "",
                                        __key: newCompanyDraft.cityID
                                    }
                                ] : []}
                                key={`company-city-select-${cities?.length || 0}`}
                                title={localize("members-form-city") || "İl"}
                                onChange={selectedItems => {
                                    const item = selectedItems[0];
                                    if (item) {
                                        setNewCompanyDraft(prev => prev ? {
                                            ...prev,
                                            cityID: item.__key,
                                            districtID: ""
                                        } : null);
                                    } else {
                                        setNewCompanyDraft(prev => prev ? {
                                            ...prev,
                                            districtID: "",
                                            cityID: ""
                                        } : null);
                                    }
                                }}
                                titleExtractor={item => item.title}
                                keyExtractor={item => item.value}
                                data={(cities || []).map(c => ({
                                    title: c.value,
                                    value: c._id
                                }))}
                                selectSheetProps={{
                                    portalName: "modal-system"
                                }}
                                spreadBehaviour="stretch"
                            />
                            <SelectBox
                                initialSelectedItems={newCompanyDraft?.districtID ? [
                                    {
                                        __title: companyDistricts?.find(d => d._id === newCompanyDraft?.districtID)?.value || "",
                                        __key: newCompanyDraft.districtID
                                    }
                                ] : []}
                                key={`company-district-select-${companyDistricts?.length || 0}`}
                                title={localize("members-form-district") || "İlçe"}
                                onChange={selectedItems => {
                                    const item = selectedItems[0];
                                    if (item) {
                                        setNewCompanyDraft(prev => prev ? {
                                            ...prev,
                                            districtID: item.__key
                                        } : null);
                                    } else {
                                        setNewCompanyDraft(prev => prev ? {
                                            ...prev,
                                            districtID: ""
                                        } : null);
                                    }
                                }}
                                data={(companyDistricts || []).map(d => ({
                                    title: d.value,
                                    value: d._id
                                }))}
                                isDisabled={!newCompanyDraft?.cityID}
                                titleExtractor={item => item.title}
                                keyExtractor={item => item.value}
                                selectSheetProps={{
                                    portalName: "modal-system"
                                }}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <TextInput
                                onChangeText={(text) => setNewCompanyDraft(prev => prev ? {
                                    ...prev,
                                    neighborhood: text
                                } : null)}
                                title={localize("members-form-neighborhood") || "Mahalle"}
                                value={newCompanyDraft?.neighborhood || ""}
                                spreadBehaviour="stretch"
                            />
                            <TextInput
                                type={(newCompanyDraft?.address || "").length > 0 && (newCompanyDraft?.address || "").trim().length < 2 ? "danger" : "default"}
                                title={localize("members-form-addressdetail") || "Adres Detayı"}
                                onChangeText={(text) => setNewCompanyDraft(prev => prev ? {
                                    ...prev,
                                    address: text
                                } : null)}
                                value={newCompanyDraft?.address || ""}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <TextInput
                                type={newCompanyDraft?.foundationYear && newCompanyDraft.foundationYear.toString().length !== 4 ? "danger" : "default"}
                                value={newCompanyDraft?.foundationYear ? newCompanyDraft.foundationYear.toString() : ""}
                                title={localize("members-form-foundationyear") || "Kuruluş Yılı"}
                                onChangeText={(text) => setNewCompanyDraft(prev => prev ? {
                                    ...prev,
                                    foundationYear: parseInt(text) || new Date().getFullYear()
                                } : null)}
                                spreadBehaviour="stretch"
                                keyboardType="numeric"
                            />
                            <TextInput
                                type={(newCompanyDraft?.website || "").length > 0 && (newCompanyDraft?.website || "").trim().length < 2 ? "danger" : "default"}
                                onChangeText={(text) => setNewCompanyDraft(prev => prev ? {
                                    ...prev,
                                    website: text
                                } : null)}
                                title={localize("members-form-website") || "Web Sitesi"}
                                value={newCompanyDraft?.website || ""}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <View
                            style={isMobile ? {
                                flexDirection: "column",
                                gap: spaces.spacingMd
                            } : [
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <TextInput
                                type={(newCompanyDraft?.aboutUs || "").length > 0 && (newCompanyDraft?.aboutUs || "").trim().length < 2 ? "danger" : "default"}
                                onChangeText={(text) => setNewCompanyDraft(prev => prev ? {
                                    ...prev,
                                    aboutUs: text
                                } : null)}
                                title={localize("members-form-aboutus") || "Hakkımızda"}
                                value={newCompanyDraft?.aboutUs || ""}
                                spreadBehaviour="stretch"
                            />
                        </View>
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    justifyContent: "space-between",
                                    gap: spaces.spacingMd,
                                    flex: 1
                                }
                            ]}
                        >
                            <Text
                                variant="bodyMediumSize"
                            >
                                {localize("members-form-isexports") || "İhracat Yapıyor mu?"}
                            </Text>
                            <Switch
                                onPress={() => setNewCompanyDraft(prev => prev ? {
                                    ...prev,
                                    isExports: !prev.isExports
                                } : null)}
                                isActive={newCompanyDraft?.isExports || false}
                                customTheme={{
                                    themeKey: "events_form" as never
                                }}
                                type="neutral"
                            />
                        </View>
                        <SelectBox
                            initialSelectedItems={(newCompanyDraft?.sectorIDs || []).map(sid => ({
                                __title: sectors?.find(s => s._id === sid)?.value || "",
                                __key: sid
                            }))}
                            placeholder={localize("members-form-sectors") || "Sektörler"}
                            onChange={selectedItems => {
                                setNewCompanyDraft(prev => prev ? {
                                    ...prev,
                                    sectorIDs: selectedItems.map(item => item.__key)
                                } : null);
                            }}
                            title={localize("members-form-sectors") || "Sektörler"}
                            key={`company-sector-select-${sectors?.length || 0}`}
                            titleExtractor={item => item.title}
                            keyExtractor={item => item.value}
                            selectSheetProps={{
                                portalName: "modal-system"
                            }}
                            data={sectors.map(s => ({
                                title: s.value,
                                value: s._id
                            }))}
                            spreadBehaviour="stretch"
                            isMultipleSelect={true}
                        />
                    </View>
                    <View
                        style={[
                            stylesheet.footerContainer,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Button
                            title={localize("members-form-cancel") || "İptal"}
                            onPress={() => setIsNewCompanyModalVisible(false)}
                            customBorderColor="secondaryButton"
                            customColor="secondaryButton"
                            spreadBehaviour="free"
                            variant="filled"
                        />
                        <Button
                            isDisabled={!newCompanyDraft?.taxNumber || !newCompanyDraft?.taxOffice || !newCompanyDraft?.address || !newCompanyDraft?.phoneNumber || !newCompanyDraft?.mail || !newCompanyDraft?.sectorIDs || newCompanyDraft.sectorIDs.length === 0 || !newCompanyDraft?.title || !newCompanyDraft?.foundationYear || !newCompanyDraft?.positionID || newCompanyDraft?.isExports === undefined || !newCompanyDraft?.aboutUs}
                            onPress={() => {
                                if (newCompanyDraft) {
                                    if (editCompanyIndex !== null) {
                                        setSelectedCompanies(prev => prev.map((c, i) => i === editCompanyIndex ? newCompanyDraft : c));
                                    } else {
                                        setSelectedCompanies(prev => [
                                            ...prev,
                                            newCompanyDraft
                                        ]);
                                    }
                                }
                                setIsNewCompanyModalVisible(false);
                            }}
                            icon={({
                                color,
                                size
                            }) => <SaveIcon
                                color={colors.project.brandWhite || colors.content.icon[color]}
                                size={size}
                            />}
                            title={localize("members-form-save") || "Kaydet"}
                            customTextColor={colors.project.brandWhite}
                            customBorderColor="gold"
                            spreadBehaviour="free"
                            customColor="gold"
                            variant="filled"
                        />
                    </View>
                </View>
            </ScrollView>
        </Modal>
        <Modal
            onClosed={() => {
                setIsEducationSheetVisible(false);
                setEditEducationIndex(null);
                setNewEducationDraft(null);
            }}
            isActive={isEducationSheetVisible}
            portalName="modal-system"
            id="new-education-modal"
        >
            <ScrollView
                style={stylesheet.container}
            >
                <View
                    style={{
                        backgroundColor: colors.project.brandWhite,
                        padding: spaces.spacingXl,
                        gap: spaces.spacingXl
                    }}
                >
                    <View
                        style={{
                            justifyContent: "space-between",
                            flexDirection: "row",
                            alignItems: "center"
                        }}
                    >
                        <Text
                            variant="titleLargeSize"
                        >
                            {localize("members-form-education") || "Eğitim Ekle"}
                        </Text>
                        <TouchableOpacity
                            onPress={() => setIsEducationSheetVisible(false)}
                        >
                            <XIcon
                                color={colors.content.icon.mid}
                                size={24}
                            />
                        </TouchableOpacity>
                    </View>
                    <View
                        key={editEducationIndex !== null ? editEducationIndex : "new"}
                        style={{
                            gap: spaces.spacingMd
                        }}
                    >
                        <SelectBox
                            initialSelectedItems={newEducationDraft?.universityID ? [
                                {
                                    __title: universities?.find(u => u._id === newEducationDraft.universityID)?.value || "",
                                    __key: newEducationDraft.universityID
                                }
                            ] : []}
                            title={localize("members-form-university") || "Üniversite"}
                            key={`university-select-${universities?.length || 0}`}
                            onChange={selectedItems => {
                                const item = selectedItems[0];
                                setNewEducationDraft(prev => ({
                                    ...(prev || {
                                        graduationYear: 0,
                                        departmentID: "",
                                        universityID: "",
                                        facultyID: ""
                                    }),
                                    universityID: item ? item.__key : "",
                                    departmentID: "",
                                    facultyID: ""
                                }));
                            }}
                            data={(universities || []).map(u => ({
                                title: u.value,
                                value: u._id
                            }))}
                            titleExtractor={item => item.title}
                            keyExtractor={item => item.value}
                            selectSheetProps={{
                                portalName: "modal-system"
                            }}
                            spreadBehaviour="stretch"
                        />
                        <SelectBox
                            initialSelectedItems={newEducationDraft?.facultyID ? [
                                {
                                    __title: faculties?.find(f => f._id === newEducationDraft.facultyID)?.value || "",
                                    __key: newEducationDraft.facultyID
                                }
                            ] : []}
                            title={localize("members-form-faculty") || "Fakülte"}
                            key={`faculty-select-${faculties?.length || 0}`}
                            isDisabled={!newEducationDraft?.universityID}
                            onChange={selectedItems => {
                                const item = selectedItems[0];
                                setNewEducationDraft(prev => ({
                                    ...(prev || {
                                        graduationYear: 0,
                                        departmentID: "",
                                        universityID: "",
                                        facultyID: ""
                                    }),
                                    facultyID: item ? item.__key : "",
                                    departmentID: ""
                                }));
                            }}
                            titleExtractor={item => item.title}
                            data={(faculties || []).map(f => ({
                                title: f.value,
                                value: f._id
                            }))}
                            keyExtractor={item => item.value}
                            selectSheetProps={{
                                portalName: "modal-system"
                            }}
                            spreadBehaviour="stretch"
                        />
                        <SelectBox
                            initialSelectedItems={newEducationDraft?.departmentID ? [
                                {
                                    __title: departments?.find(d => d._id === newEducationDraft.departmentID)?.value || "",
                                    __key: newEducationDraft.departmentID
                                }
                            ] : []}
                            title={localize("members-form-department") || "Bölüm"}
                            key={`department-select-${departments?.length || 0}`}
                            onChange={selectedItems => {
                                const item = selectedItems[0];
                                setNewEducationDraft(prev => ({
                                    ...(prev || {
                                        graduationYear: 0,
                                        departmentID: "",
                                        universityID: "",
                                        facultyID: ""
                                    }),
                                    departmentID: item ? item.__key : ""
                                }));
                            }}
                            isDisabled={!newEducationDraft?.facultyID}
                            data={(departments || []).map(d => ({
                                title: d.value,
                                value: d._id
                            }))}
                            titleExtractor={item => item.title}
                            keyExtractor={item => item.value}
                            selectSheetProps={{
                                portalName: "modal-system"
                            }}
                            spreadBehaviour="stretch"
                        />
                        <TextInput
                            type={newEducationDraft?.graduationYear && newEducationDraft.graduationYear.toString().length !== 4 ? "danger" : "default"}
                            title={localize("members-form-graduationyear") || "Mezuniyet Yılı"}
                            value={newEducationDraft?.graduationYear?.toString() || ""}
                            onChangeText={(text) => setNewEducationDraft(prev => ({
                                ...(prev || {
                                    graduationYear: 0,
                                    departmentID: "",
                                    universityID: "",
                                    facultyID: ""
                                }),
                                graduationYear: Number(text) || 0
                            }))}
                            spreadBehaviour="stretch"
                        />
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    justifyContent: "flex-end"
                                }
                            ]}
                        >
                            <Button
                                isDisabled={!(newEducationDraft?.universityID && newEducationDraft?.facultyID && newEducationDraft?.departmentID)}
                                onPress={() => {
                                    if (newEducationDraft) {
                                        if (editEducationIndex !== null) {
                                            setFormData(prev => ({
                                                ...prev,
                                                education: prev.education?.map((e, i) => i === editEducationIndex ? newEducationDraft : e)
                                            }));
                                        } else {
                                            setFormData(prev => ({
                                                ...prev,
                                                education: [
                                                    ...(prev.education || []),
                                                    newEducationDraft
                                                ]
                                            }));
                                        }
                                    }
                                    setIsEducationSheetVisible(false);
                                }}
                                icon={({
                                    color,
                                    size
                                }) => <SaveIcon
                                    color={colors.project.brandWhite || colors.content.icon[color]}
                                    size={size}
                                />}
                                title={localize("members-form-save") || "Kaydet"}
                                customTextColor={colors.project.brandWhite}
                                customBorderColor="gold"
                                spreadBehaviour="free"
                                customColor="gold"
                                variant="filled"
                            />
                        </View>
                    </View>
                </View>
            </ScrollView>
        </Modal>
    </Fragment>;
};

export default MemberCreateModal;
